import React from "react";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { BandTemplate, LocalBandTemplate, floorPlanExtractionActions } from "../../../reducers/floorPlanExtraction";
import { LeadPencilIcon, TrashIcon } from "../../SVGIcons";

interface BandTemplateSelectPopupProps {
  bandTemplates: BandTemplate[];
  selectedBandTemplate: BandTemplate | null;
  localBandTemplate: LocalBandTemplate | null;
  bandTemplatesCount: number;
  bandTemplatesMaxCount: number;
  onClose: () => void;
  onSelectTemplate: (template: BandTemplate) => void;
  onEditTemplate: (template: BandTemplate) => void;
  onDeleteTemplate: (templateId: number) => void;
  onNewTemplate: () => void;
}

export const BandTemplateSelectPopup: React.FC<BandTemplateSelectPopupProps> = ({
  bandTemplates,
  selectedBandTemplate,
  localBandTemplate,
  bandTemplatesCount,
  bandTemplatesMaxCount,
  onClose,
  onSelectTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onNewTemplate,
}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: '520px',
          maxWidth: '90%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* ヘッダー */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: '#333', margin: 0 }}>
            帯テンプレートを選択
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666',
              padding: 0,
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* テンプレート一覧 */}
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* なし選択 */}
            <button
              onClick={() => {
                dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
                dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
                onClose();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                border: !selectedBandTemplate && !localBandTemplate ? '2px solid #f97316' : '1px solid #e5e7eb',
                borderRadius: 8,
                background: !selectedBandTemplate && !localBandTemplate ? '#fff7ed' : '#fff',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <span style={{ color: '#6b7280', fontSize: 14 }}>なし（帯を使用しない）</span>
            </button>

            {/* 登録済みテンプレート */}
            {bandTemplates.map((template) => (
              <div
                key={template.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  border: selectedBandTemplate?.id === template.id ? '2px solid #f97316' : '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: selectedBandTemplate?.id === template.id ? '#fff7ed' : '#fff',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  onSelectTemplate(template);
                  onClose();
                }}
              >
                {/* サムネイル */}
                <div style={{
                  width: 80,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img
                    src={template.image_url}
                    alt={template.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
                {/* 名前と位置 */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {template.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    {template.position === 'top' ? '上部' : '下部'}
                  </div>
                </div>
                {/* 編集ボタン */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditTemplate(template);
                  }}
                  style={{
                    padding: 6,
                    border: 'none',
                    borderRadius: 4,
                    background: '#e0f2fe',
                    color: '#0284c7',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="編集"
                >
                  <LeadPencilIcon style={{ width: 16, height: 16 }} />
                </button>
                {/* 削除ボタン */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteTemplate(template.id);
                  }}
                  style={{
                    padding: 6,
                    border: 'none',
                    borderRadius: 4,
                    background: '#fca5a5',
                    color: '#991b1b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="削除"
                >
                  <TrashIcon style={{ width: 16, height: 16 }} />
                </button>
              </div>
            ))}

            {/* ローカルテンプレート（一時的） */}
            {localBandTemplate && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  border: '2px solid #3b82f6',
                  borderRadius: 8,
                  background: '#eff6ff'
                }}
              >
                <div style={{
                  width: 80,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img
                    src={localBandTemplate.imageUrl}
                    alt="一時的な帯"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, color: '#2563eb' }}>一時的な帯</div>
                  <div style={{ fontSize: 12, color: '#60a5fa' }}>未保存</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* アクションボタン */}
        <div style={{ display: 'flex', gap: 12, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          {/* 新規テンプレート登録 */}
          {bandTemplatesCount < bandTemplatesMaxCount && (
            <button
              onClick={onNewTemplate}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px 16px',
                border: '1px solid #3b82f6',
                borderRadius: 6,
                background: '#fff',
                color: '#3b82f6',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              <span style={{ fontSize: 16 }}>+</span>
              新規登録
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BandTemplateSelectPopup;
